import React, { ChangeEvent, useState } from 'react';

import { FormInput, TextInput } from '@/components/shared/Inputs';
import { SingleActionLayout } from '@/components/shared/SingleActionLayout/SingleActionLayout';
import { useSendCodeMutation } from '@/graphql';
import { FetchError } from '@/graphql/codegen-fetcher';
import { classNames } from '@/helpers/classNames';
import { useAuth } from '@/providers/AuthProvider';
import { Divider, FormHelperText } from '@material-ui/core';
import { Formik } from 'formik';
import * as Yup from 'yup';

import { AccountDisplayProps, EAccountDisplayState } from '../../AccountPanel';
import styles from './DeleteAccount.module.scss';

const validationSchema = Yup.object().shape({
	email: Yup.string()
		.email('Must be a valid email address')
		.required('Email address is required'),
});

export const DeleteAccount: React.FC<AccountDisplayProps> = ({
	pushDisplayState,
}) => {
	const [admin, setAdmin] = useState<boolean>(false);
	const { mutateAsync, error: sendError } = useSendCodeMutation<FetchError>();
	const { user } = useAuth();

	const handleToggle = (event: ChangeEvent<HTMLInputElement>) => {
		setAdmin(event.target.checked);
	};

	return (
		<Formik
			initialValues={{
				email: '',
			}}
			validationSchema={validationSchema}
			onSubmit={async (values, formikHelpers) => {
				if (values.email !== user.email) {
					return formikHelpers.setFieldError(
						'email',
						'Account email and provided email do not match',
					);
				}
				try {
					await mutateAsync({});
					pushDisplayState(
						EAccountDisplayState.DELETE_ACCOUNT_VERIFICATION,
					);
				} catch (error) {
					return console.log(`Error sending invite`, error);
				}
			}}
		>
			{({ handleSubmit, handleChange, values, isSubmitting, errors }) => (
				<SingleActionLayout
					actionLabel="Get verification code"
					actionDisabled={isSubmitting}
					onActionClick={handleSubmit}
					noPadding
				>
					<div
						className={classNames([
							styles['delete-account__top-content'],
							styles['content'],
						])}
					>
						To delete your account, please enter the email
						associated with your account to receive a verification
						code.
						<br />
						<br />
						When we delete your account, we anonymize your personal
						activity (such as comments and messages) and destroy
						personal information like your name and email from our
						system.
					</div>
					<Divider />

					<form onSubmit={handleSubmit} className={styles['content']}>
						<FormInput
							label="Confirm email associated with this account"
							id="email"
							name="email"
							type="email"
							placeholder="Email"
							onChange={handleChange}
							value={values.email}
							disabled={isSubmitting}
							fullWidth
						/>

						<div
							style={{
								visibility:
									errors.email || sendError
										? 'visible'
										: 'hidden',
							}}
						>
							<FormHelperText error={true}>
								{errors.email ||
									sendError?.message ||
									'Placeholder'}
							</FormHelperText>
						</div>
						<div className="vsb-20" />

						<div
							className={`${styles['delete-account__warning']} text-center`}
						>
							Account deletion is permenent and CANNOT be
							restored. Please proceed with CAUTION.
						</div>
					</form>
				</SingleActionLayout>
			)}
		</Formik>
	);
};
